// Definition ID: def:rosta:ledger:donation
// POC Donate screen — exercises the generic payment provider (StreetWallet iframe)
// without coupling the UI to any single payment method.

import { useState, useEffect } from 'react';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton, IonInput, IonLabel, IonItem, IonNote, IonSpinner, IonIcon } from '@ionic/react';
import { heart, checkmarkCircle, closeCircle, alertCircle } from 'ionicons/icons';
import { getPaymentProvider, activeProviderId } from '@/lib/payments';
import { generateReference, type PaymentResult } from '@/lib/payments/types';
import { setSeo, resetSeo, BASE_URL } from '@/lib/seo';

type Phase = 'idle' | 'paying' | 'success' | 'failed' | 'cancelled';

export function DonatePage() {
  const [amount, setAmount] = useState('50');
  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<PaymentResult | null>(null);

  useEffect(() => {
    setSeo({
      title: 'Support BluePages | Donate',
      description:
        'Support BluePages — South Africa’s skills-first job marketplace. Your donation helps keep connecting skilled people with contract and project work free.',
      canonical: `${BASE_URL}/donate`,
    });
    return () => resetSeo();
  }, []);

  const numericAmount = Number(amount);
  const valid = Number.isFinite(numericAmount) && numericAmount > 0;

  const handleDonate = async () => {
    if (!valid) return;
    setPhase('paying');
    setResult(null);
    try {
      const provider = getPaymentProvider();
      const res = await provider.pay({
        reference: generateReference('donate'),
        amount: numericAmount,
        description: `BluePages donation via ${provider.id}`,
      });
      setResult(res);
      setPhase(res.status === 'success' ? 'success' : res.status === 'cancelled' ? 'cancelled' : 'failed');
    } catch {
      setPhase('cancelled');
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Donate</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
          <IonIcon icon={heart} style={{ fontSize: '2.5rem', color: 'var(--ion-color-danger)' }} />
          <h2 className="bp-subheading">Support BluePages</h2>
          <p className="bp-subheading" style={{ opacity: 0.8 }}>
            This is a proof-of-concept payment flow. No real charge is made in demo mode.
            Provider: <strong>{activeProviderId}</strong>
          </p>

          <IonItem>
            <IonLabel position="stacked">Amount (ZAR)</IonLabel>
            <IonInput
              type="number"
              value={amount}
              onIonInput={(e) => setAmount(e.detail.value ?? '')}
              min={1}
              disabled={phase === 'paying'}
            />
          </IonItem>
          {!valid && <IonNote color="danger">Enter an amount greater than 0.</IonNote>}

          <div style={{ marginTop: 16 }}>
            <IonButton expand="block" disabled={!valid || phase === 'paying'} onClick={handleDonate}>
              {phase === 'paying' ? <IonSpinner name="crescent" /> : 'Donate R' + (valid ? numericAmount : '0')}
            </IonButton>
          </div>

          {phase === 'success' && result && (
            <p style={{ color: 'var(--ion-color-success)' }}>
              <IonIcon icon={checkmarkCircle} /> Thank you! R{result.amount} donated (ref: {result.reference}).
            </p>
          )}
          {phase === 'failed' && (
            <p style={{ color: 'var(--ion-color-danger)' }}>
              <IonIcon icon={closeCircle} /> Payment failed. Please try again.
            </p>
          )}
          {phase === 'cancelled' && (
            <p style={{ color: 'var(--ion-color-warning)' }}>
              <IonIcon icon={alertCircle} /> Payment cancelled.
            </p>
          )}

          {(phase === 'success' || phase === 'failed') && (
            <IonButton fill="clear" onClick={() => setPhase('idle')}>
              Make another donation
            </IonButton>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
